const TEXT_INPUT_TYPES = new Set([
  "text",
  "search",
  "email",
  "tel",
  "url",
  "password",
  "number",
  "",
]);

export const VOICE_FIELD_DICTATION_EVENT = "rcm-voice-field-dictation";

let focusedFieldRef: HTMLElement | null = null;

type EditableHTMLElement =
  | HTMLInputElement
  | HTMLTextAreaElement
  | (HTMLElement & { isContentEditable: true });

export function isEditableElement(element: Element | null): element is EditableHTMLElement {
  if (!element || !(element instanceof HTMLElement)) return false;
  if (element.isContentEditable) {
    return element.getAttribute("contenteditable") !== "false";
  }
  if (element instanceof HTMLTextAreaElement) {
    return !element.disabled && !element.readOnly;
  }
  if (element instanceof HTMLInputElement) {
    if (element.disabled || element.readOnly) return false;
    return TEXT_INPUT_TYPES.has(element.type);
  }
  return false;
}

/** Resolves MUI wrappers to the actual input/textarea when possible. */
export function resolveEditableTarget(target: EventTarget | null): HTMLElement | null {
  if (!target || !(target instanceof HTMLElement)) return null;
  if (isEditableElement(target)) return target;

  if (target instanceof HTMLLabelElement) {
    const id = target.htmlFor;
    if (id) {
      const linked = document.getElementById(id);
      if (isEditableElement(linked)) return linked;
    }
  }

  const nested = target.querySelector<HTMLElement>(
    "input:not([type=checkbox]):not([type=radio]):not([type=hidden]):not([disabled]):not([readonly]), textarea:not([disabled]):not([readonly]), [contenteditable='true']",
  );
  if (isEditableElement(nested)) return nested;

  return null;
}

export function getFocusedEditableField(): HTMLElement | null {
  if (focusedFieldRef && document.contains(focusedFieldRef)) {
    return focusedFieldRef;
  }
  focusedFieldRef = null;
  return null;
}

export function setFocusedEditableField(element: HTMLElement | null): void {
  if (focusedFieldRef) {
    focusedFieldRef.removeAttribute("data-rcm-voice-field-active");
  }

  focusedFieldRef = element && isEditableElement(element) ? element : null;

  if (focusedFieldRef) {
    focusedFieldRef.setAttribute("data-rcm-voice-field-active", "true");
  }

  window.dispatchEvent(
    new CustomEvent<{ active: boolean }>(VOICE_FIELD_DICTATION_EVENT, {
      detail: { active: Boolean(focusedFieldRef) },
    }),
  );
}

function setNativeElementValue(
  element: HTMLInputElement | HTMLTextAreaElement,
  value: string,
) {
  const prototype =
    element instanceof HTMLTextAreaElement
      ? window.HTMLTextAreaElement.prototype
      : window.HTMLInputElement.prototype;
  const descriptor = Object.getOwnPropertyDescriptor(prototype, "value");
  descriptor?.set?.call(element, value);
  element.dispatchEvent(new Event("input", { bubbles: true }));
  element.dispatchEvent(new Event("change", { bubbles: true }));
}

export function insertTextIntoFocusedField(text: string): boolean {
  const element = getFocusedEditableField();
  const trimmed = text.trim();
  if (!element || !trimmed) return false;

  if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
    const start = element.selectionStart ?? element.value.length;
    const end = element.selectionEnd ?? element.value.length;
    const before = element.value.slice(0, start);
    const after = element.value.slice(end);
    const needsSpace = before.length > 0 && !/\s$/.test(before);
    const insertion = `${needsSpace ? " " : ""}${trimmed}`;
    const nextValue = `${before}${insertion}${after}`;
    const cursor = before.length + insertion.length;

    setNativeElementValue(element, nextValue);
    element.focus();
    element.setSelectionRange(cursor, cursor);
    return true;
  }

  if (element.isContentEditable) {
    element.focus();
    document.execCommand("insertText", false, trimmed);
    element.dispatchEvent(new Event("input", { bubbles: true }));
    return true;
  }

  return false;
}
